/**
 * Channel Console — LinkedIn module (A7Y-3779).
 *
 * Same two layers as the Website and Product modules: a headline strip that
 * answers "how is LinkedIn doing" in about five seconds, then the detail —
 * impressions for the last eight posts, and a recent-posts table whose
 * Account column makes personal-profile vs company-page legible at a glance
 * (the ticket's explicit requirement).
 *
 * States rendered EXPLICITLY rather than as zeros, mirroring
 * `WebsiteModule.tsx`'s enumeration:
 *
 *   - `configured: false` — the AuthoredUp Worker secret is absent here.
 *     Distinct from "no posts".
 *   - `matchedPosts === 0` with `queriedPosts > 0` — posts came back but none
 *     resolved to a connected account, i.e. the upstream response shape
 *     changed. The tripwire analogous to the Website module's `matchedEvents`.
 *   - no snapshot at all — split on whether a pull has ever succeeded.
 *   - `postsWithoutImpressions > 0` — an inline note, because those posts are
 *     excluded from the engagement average and a silently smaller denominator
 *     is exactly the confident-wrong number this page must not show.
 *   - `freshness.stale` — additive banner over the last good numbers, never
 *     an early return.
 *
 * Why the numbers here will not match the AuthoredUp UI exactly: engagement
 * rate is computed from the post's own counts rather than read from the
 * API's `engagement_rate` field, which contradicts them (measured: `er: 0`
 * on a post with 18 reactions over 29 impressions). The card says so.
 */
import type { ChannelConsoleLinkedInData, LinkedInPostRow } from './types';
import { Card, Notice, formatInt, formatLocal, formatPercent } from './ui';

function accountLabel(account: LinkedInPostRow['account']): string {
  return account === 'profile' ? 'Profile' : 'Page';
}

function postDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function LinkedInModule({
  data,
  loading,
  error,
}: {
  data: ChannelConsoleLinkedInData | undefined;
  loading: boolean;
  error: string | null;
}) {
  if (error) {
    return (
      <Notice tone="warn" title="LinkedIn module failed to load">
        {error}
      </Notice>
    );
  }
  if (loading && !data) {
    return <div className="text-sm text-zinc-500">Loading LinkedIn data…</div>;
  }
  if (!data) return null;

  // `configured:false` means the credential is absent, i.e. nothing can be
  // REFRESHED. It does not mean there is no data. Suppressing a real stored
  // snapshot because it cannot be refreshed withholds numbers that are true
  // (Karen round 2, F4/F6) — so the early return fires only when there is
  // genuinely nothing to show, and the banner below covers the rest.
  if (!data.configured && data.snapshotAt === null) {
    return (
      <Notice tone="warn" title="LinkedIn module not configured">
        The AuthoredUp API key is not set as a Worker secret in this environment, and no snapshot
        has ever been stored — so there is nothing to show and nothing to fetch. This is a
        provisioning gap, not an absence of posts.
      </Notice>
    );
  }

  // Posts came back but none resolved to a connected account — the upstream
  // shape changed. Loud panel, never a plausible zero.
  if (data.matchedPosts === 0 && data.queriedPosts > 0) {
    return (
      <Notice tone="warn" title="No posts matched a connected account">
        AuthoredUp returned {formatInt(data.queriedPosts)} posts, none of which carried a known
        profile or company actor. The upstream response shape has most likely changed.
      </Notice>
    );
  }

  if (data.snapshotAt === null) {
    return data.freshness.lastError === null ? (
      <Notice tone="info" title="No snapshot pulled yet">
        The nightly pull captures a snapshot on its own; Refresh captures one now.
      </Notice>
    ) : (
      <Notice tone="warn" title="Snapshot not captured">
        No snapshot exists and the last pull failed. Refresh retries now.
        <div className="mt-1 font-mono text-xs text-red-700 dark:text-red-400">
          {data.freshness.lastError}
        </div>
      </Notice>
    );
  }

  const { headline, recentPosts, impressionsByPost } = data;
  const maxImpressions = Math.max(1, ...impressionsByPost.map((p) => p.impressions ?? 0));

  return (
    <div className="space-y-6">
      {!data.configured && (
        <Notice tone="warn" title="These numbers cannot be refreshed">
          The AuthoredUp API key is not set as a Worker secret in this environment. The snapshot
          below is real and was stored at {formatLocal(data.snapshotAt)}, but nothing can update it
          — Refresh will not reach AuthoredUp until the credential is provisioned.
        </Notice>
      )}

      {data.freshness.stale && (
        <Notice tone="warn" title="Data is stale">
          {data.freshness.lastSuccessAt === null
            ? 'No successful pull yet.'
            : `Last successful pull ${formatLocal(data.freshness.lastSuccessAt)}.`}
          {data.freshness.lastError && (
            <div className="mt-1 font-mono text-xs text-red-700 dark:text-red-400">
              {data.freshness.lastError}
            </div>
          )}
        </Notice>
      )}

      {/* Layer 1 — the five-second scan. */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card
          title="Posts (7d)"
          primary={formatInt(headline.posts7d)}
          secondary="Profile + company page"
        />
        <Card
          title="Avg. engagement (7d)"
          primary={
            headline.avgEngagement7d === null ? '—' : formatPercent(headline.avgEngagement7d)
          }
          secondary="Computed from reactions, comments and shares"
        />
        {/* Deliberately NOT a 7-day figure, unlike the two cards to its left.
            The ticket's headline list gives "best post" no period and the
            confirmed mockup shows one from outside its own "Posts, 7d" count.
            The window is named on the card so the number is not read as a
            weekly one (Karen round 1, Medium). */}
        <Card
          title="Best post"
          primary={
            headline.bestPostImpressions === null
              ? '—'
              : `${formatInt(headline.bestPostImpressions)} impr.`
          }
          secondary={
            headline.bestPostExcerpt
              ? `Across all ${formatInt(data.matchedPosts)} posts — ${headline.bestPostExcerpt}`
              : `Across all ${formatInt(data.matchedPosts)} posts in the snapshot`
          }
        />
        <Card
          title="Followers"
          primary={headline.followersTotal === null ? '—' : formatInt(headline.followersTotal)}
          secondary={
            data.followersProfile !== null && data.followersPage !== null
              ? `${formatInt(data.followersProfile)} profile + ${formatInt(data.followersPage)} page`
              : 'Profile + page'
          }
        />
      </div>

      {data.postsWithoutImpressions > 0 && (
        <Notice tone="info" title="Some posts report no impressions">
          {data.postsWithoutImpressions} of the last 7 days&rsquo; {formatInt(headline.posts7d)}{' '}
          {headline.posts7d === 1 ? 'post' : 'posts'} came back with no impression count. They are
          excluded from the engagement average rather than counted as zero, which would understate
          it.
        </Notice>
      )}

      {/* Layer 2 — the detail. */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            Impressions by post
          </h3>
          <p className="mt-0.5 text-xs text-zinc-500">
            Last {impressionsByPost.length} {impressionsByPost.length === 1 ? 'post' : 'posts'},
            oldest first
          </p>
          {impressionsByPost.length === 0 ? (
            <p className="mt-4 text-sm text-zinc-500">No posts in this snapshot.</p>
          ) : (
            <div
              className="mt-4 flex h-40 items-end gap-1.5"
              role="img"
              aria-label="Impressions for the most recent LinkedIn posts"
            >
              {impressionsByPost.map((post) => (
                <div
                  key={post.urn}
                  className={
                    post.impressions === null
                      ? // "Not reported" must not look like "almost none": a
                        // `?? 0` bar is visually identical to a genuinely tiny
                        // one, and hover text is not disclosure (Karen round 1,
                        // Low). A dashed outline reads as absent data.
                        'flex-1 rounded-t border border-dashed border-zinc-400 dark:border-zinc-500'
                      : 'flex-1 rounded-t bg-blue-500/80 dark:bg-blue-400/80'
                  }
                  style={{
                    height:
                      post.impressions === null
                        ? '100%'
                        : `${Math.max(2, (post.impressions / maxImpressions) * 100)}%`,
                  }}
                  title={`${postDate(post.publishedAt)} · ${accountLabel(post.account)}: ${
                    post.impressions === null
                      ? 'impressions not reported'
                      : `${formatInt(post.impressions)} impressions`
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Recent posts</h3>
          <p className="mt-0.5 text-xs text-zinc-500">
            Everything posted from the two connected accounts, unfiltered.
          </p>
          {recentPosts.length === 0 ? (
            <p className="mt-4 text-sm text-zinc-500">No posts in this snapshot.</p>
          ) : (
            <table className="mt-4 w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
                  <th className="pb-2 font-medium">Date</th>
                  <th className="pb-2 font-medium">Account</th>
                  <th className="pb-2 font-medium">Post</th>
                  <th className="pb-2 text-right font-medium">Impr.</th>
                  <th className="pb-2 text-right font-medium">ER</th>
                </tr>
              </thead>
              <tbody>
                {recentPosts.map((post) => (
                  <tr key={post.urn} className="border-t border-zinc-100 dark:border-zinc-800">
                    <td className="whitespace-nowrap py-1.5 text-zinc-700 dark:text-zinc-300">
                      {postDate(post.publishedAt)}
                    </td>
                    <td className="whitespace-nowrap py-1.5 font-mono text-xs text-zinc-500">
                      {accountLabel(post.account)}
                    </td>
                    <td
                      className="max-w-[16rem] truncate py-1.5 text-zinc-700 dark:text-zinc-300"
                      title={post.excerpt}
                    >
                      {post.excerpt}
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-zinc-900 dark:text-zinc-100">
                      {post.impressions === null ? '—' : formatInt(post.impressions)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-zinc-900 dark:text-zinc-100">
                      {post.engagementRate === null ? '—' : formatPercent(post.engagementRate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
